import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Linkedin, Facebook, Apple } from "lucide-react";
import { SOCIAL, TAGLINES } from "./constants";
import { loadMarketingAnalytics } from "./analytics";

/**
 * Marketing-side header. Soft blush gradient, serif wordmark, sticky on
 * scroll. Mobile reveals a slide-down sheet so the rest of the surface
 * keeps its quiet, uncluttered feel.
 */
function Header() {
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/resources", label: "Resources" },
    { to: "/about", label: "About Us" },
    { to: "/safety-privacy", label: "Safety & Privacy" },
    { to: "/partners", label: "Partners" },
    { to: "/volunteer", label: "Volunteer" },
    { to: "/press", label: "Press" },
    { to: "/donate", label: "Donate" },
    { to: "/wall", label: "Quiet Wall" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-md border-b border-brand/10">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="marketing-logo" className="font-serif italic text-2xl text-brand-deep tracking-tight">
          sister signal
        </Link>
        <nav className="hidden lg:flex items-center gap-8" aria-label="primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`marketing-nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive ? "text-brand" : "text-foreground/70 hover:text-brand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/app"
            data-testid="marketing-open-app"
            className="text-sm px-5 py-2 rounded-full bg-brand text-white hover:bg-brand/90 transition-all"
          >
            Open Web App
          </Link>
        </nav>
        <button
          aria-label="Open menu"
          data-testid="marketing-menu-toggle"
          className="lg:hidden text-foreground/70 hover:text-brand"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-brand/10 bg-background" data-testid="marketing-menu-mobile">
          <nav className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-3" aria-label="mobile">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`marketing-mobile-nav-${l.to.slice(1)}`}
                className="text-base text-foreground/80 py-2"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/app"
              onClick={() => setOpen(false)}
              className="mt-2 text-center px-5 py-3 rounded-full bg-brand text-white"
            >
              Open Web App
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

/** TikTok glyph — lucide-react doesn't include it, so a tight inline SVG. */
function TiktokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.6 5.82a4.28 4.28 0 0 1-3.77-3.82h-3.06v13.66a2.6 2.6 0 1 1-2.6-2.6c.27 0 .53.04.78.12V9.96A5.94 5.94 0 1 0 12.83 16V8.43a7.36 7.36 0 0 0 4.4 1.45V6.7a4.3 4.3 0 0 1-.63-.88Z" />
    </svg>
  );
}

/** Threads glyph — also missing from lucide. Stroke-based to match. */
function ThreadsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.2 2c5.4 0 9.5 3.4 9.5 9.8s-3.9 10.2-9.4 10.2c-2.4 0-4.5-.6-6-1.9l1.4-2c1.1.9 2.6 1.4 4.4 1.4 3.9 0 6.4-2.5 6.7-6.4-.9 1.4-2.7 2.3-4.9 2.3-3.2 0-5.5-1.7-5.5-4.4 0-2.8 2.5-4.8 5.9-4.8 1.9 0 3.5.7 4.5 1.9-.5-2.7-2.6-4.1-5.6-4.1-2.1 0-3.7.7-4.8 2L4.7 4C6.4 2.5 8.7 2 12.2 2Zm-.3 7.3c-2 0-3.4 1-3.4 2.5 0 1.4 1.3 2.3 3.2 2.3 2 0 3.4-1 3.4-2.5 0-1.4-1.2-2.3-3.2-2.3Z" />
    </svg>
  );
}

function Footer() {
  return (
    <footer className="border-t border-brand/10 bg-brand-soft/30 mt-24">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="font-serif italic text-3xl text-brand-deep">sister signal</div>
          <p className="mt-3 text-foreground/70 max-w-md leading-relaxed">{TAGLINES.secondary}</p>
          <div className="mt-5 flex items-center gap-3">
            <a href={SOCIAL.appStore} target="_blank" rel="noopener noreferrer"
               data-testid="footer-app-store"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground/90 text-background text-sm hover:bg-foreground transition-all">
              <Apple className="w-4 h-4" /> Download on App Store
            </a>
            <Link to="/app" data-testid="footer-open-app"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-white text-sm hover:bg-brand/90 transition-all">
              Open Web App
            </Link>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-foreground/55">Sister Signal</p>
          <ul className="mt-3 space-y-2 text-sm text-foreground/75">
            <li><Link to="/about" className="hover:text-brand">About Us</Link></li>
            <li><Link to="/safety-privacy" className="hover:text-brand">Safety & Privacy</Link></li>
            <li><Link to="/resources" className="hover:text-brand">Resources</Link></li>
            <li><Link to="/partners" className="hover:text-brand">Partners</Link></li>
            <li><Link to="/volunteer" className="hover:text-brand">Volunteer</Link></li>
            <li><Link to="/press" className="hover:text-brand">Press</Link></li>
            <li><Link to="/donate" className="hover:text-brand">Donate</Link></li>
            <li><Link to="/wall" className="hover:text-brand">Quiet Wall</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-foreground/55">Legal</p>
          <ul className="mt-3 space-y-2 text-sm text-foreground/75">
            <li><Link to="/privacy" className="hover:text-brand">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-brand">Terms of Service</Link></li>
            <li><Link to="/contact" className="hover:text-brand">Contact</Link></li>
          </ul>
          <p className="text-xs uppercase tracking-widest text-foreground/55 mt-6">Follow</p>
          <div className="mt-3 flex items-center gap-3">
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
               className="grid place-items-center w-9 h-9 rounded-full bg-card border border-border text-foreground/70 hover:text-brand hover:border-brand/40 transition-all">
              <Instagram className="w-4 h-4" />
            </a>
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin"
               className="grid place-items-center w-9 h-9 rounded-full bg-card border border-border text-foreground/70 hover:text-brand hover:border-brand/40 transition-all">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
               className="grid place-items-center w-9 h-9 rounded-full bg-card border border-border text-foreground/70 hover:text-brand hover:border-brand/40 transition-all">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok"
               className="grid place-items-center w-9 h-9 rounded-full bg-card border border-border text-foreground/70 hover:text-brand hover:border-brand/40 transition-all">
              <TiktokIcon className="w-4 h-4" />
            </a>
            <a href={SOCIAL.threads} target="_blank" rel="noopener noreferrer" aria-label="Threads" data-testid="footer-threads"
               className="grid place-items-center w-9 h-9 rounded-full bg-card border border-border text-foreground/70 hover:text-brand hover:border-brand/40 transition-all">
              <ThreadsIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-brand/10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-6 text-xs text-foreground/55 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Sister Signal. Built from lived experience.</span>
          <span>If you are in immediate danger, call 911. National DV Hotline: 1-800-799-7233.</span>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingShell({ children }) {
  // Scroll-to-top on route change so a long resource page doesn't keep
  // the next page anchored partway down.
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);

  // Lazy-load analytics ONLY on the marketing surface — the app stays
  // tracker-free per Sister Signal's privacy commitment. No-op if no
  // REACT_APP_PLAUSIBLE_DOMAIN / REACT_APP_GA4_ID is configured.
  React.useEffect(() => { loadMarketingAnalytics(); }, []);

  return (
    <div className="min-h-[100dvh] bg-background text-foreground antialiased">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
